import React, { useEffect, useMemo, useState } from "react"
import clsx from "clsx"
import { ICandidatSuiviModel } from "@common-models/*"
import MyTooltip from "@common-utils/MyTooltip"
import { useAuth } from "../../../../../AppContext"

interface Props {
     item: ICandidatSuiviModel
     onUpdateFeeling: (feeling: string | null, commentaire?: string) => void
}

const CandidatSuiviFeelingAction = ({ item, onUpdateFeeling }: Props) => {
     const { currentUser } = useAuth()

     // Identification de l'avis de l'utilisateur actuel
     const myFeelingObj = useMemo(() => item.feelings?.find(f => f.personnel?.id === currentUser?.personnel?.id), [item.feelings, currentUser?.personnel?.id])
     const otherFeelings = useMemo(() => item.feelings?.filter(f => f.personnel?.id !== currentUser?.personnel?.id) || [], [item.feelings, currentUser?.personnel?.id])

     const [tempComment, setTempComment] = useState(myFeelingObj?.commentaire || "")
     const [isEditingComment, setIsEditingComment] = useState(false)

     useEffect(() => {
          setTempComment(myFeelingObj?.commentaire || "")
     }, [myFeelingObj?.commentaire])

     const feelingsConfig = [
          { id: "FEELING_LIKE", icon: "fa-thumbs-up", color: "success", label: "Positif" },
          { id: "FEELING_NEUTRAL", icon: "fa-meh", color: "warning", label: "Neutre" },
          { id: "FEELING_DISLIKE", icon: "fa-thumbs-down", color: "danger", label: "Négatif" },
     ]

     const handleToggle = (feelingId: string) => {
          if (myFeelingObj?.feeling === feelingId) {
               onUpdateFeeling(null, "")
               setIsEditingComment(false)
          } else {
               onUpdateFeeling(feelingId, tempComment)
               setIsEditingComment(true)
          }
     }

     const handleSaveComment = () => {
          onUpdateFeeling(myFeelingObj?.feeling || null, tempComment)
          setIsEditingComment(false)
     }

     return (
          <div className="mt-5 border-top border-gray-100 pt-4">
               {/* SECTION : VOTRE AVIS */}
               <div className="d-flex align-items-center mb-4">
                    <span className="fs-8 fw-boldest text-gray-600 text-uppercase me-4">Votre ressenti :</span>
                    <div className="d-flex gap-2">
                         {feelingsConfig.map(f => (
                              <MyTooltip key={f.id} title={f.label}>
                                   <button
                                        onClick={() => handleToggle(f.id)}
                                        className={clsx(
                                             "btn btn-icon btn-sm btn-circle transition-all",
                                             myFeelingObj?.feeling === f.id ? `btn-${f.color} shadow-sm scale-110` : "btn-light"
                                        )}
                                   >
                                        <i className={clsx("fas", f.icon, myFeelingObj?.feeling === f.id ? "text-white" : `text-${f.color}`)} />
                                   </button>
                              </MyTooltip>
                         ))}
                    </div>
               </div>

               {/* CHAMP DE COMMENTAIRE INTUITIF */}
               {(isEditingComment || myFeelingObj) && (
                    <div className="position-relative mb-6 animate__animated animate__fadeIn">
                         <textarea
                              className="form-control form-control-solid fs-7 p-4 pe-15 bg-light rounded-3 border-gray-200"
                              placeholder="Écrivez votre ressenti ici..."
                              value={tempComment}
                              onChange={e => {
                                   setTempComment(e.target.value)
                                   setIsEditingComment(true)
                              }}
                              rows={2}
                              style={{ resize: "none" }}
                         />
                         {/* Bouton de validation explicite pour plus d'intuition */}
                         {(isEditingComment || tempComment !== myFeelingObj?.commentaire) && (
                              <button
                                   onClick={handleSaveComment}
                                   className="btn btn-icon btn-sm btn-primary position-absolute bottom-0 end-0 m-2 shadow-sm pulse pulse-white"
                                   title="Enregistrer le commentaire"
                              >
                                   <i className="fas fa-check fs-6"></i>
                                   <span className="pulse-ring"></span>
                              </button>
                         )}
                    </div>
               )}

               {/* SECTION : AVIS DES COLLÈGUES (Plus lisible) */}
               {otherFeelings.length > 0 && (
                    <div className="mt-4 bg-gray-50 rounded-3 p-4 border border-dashed border-gray-300">
                         <h6 className="fs-9 fw-boldest text-gray-400 text-uppercase mb-3">Autres ressentis :</h6>
                         <div className="d-flex flex-column gap-3">
                              {otherFeelings.map((f, idx) => {
                                   const config = feelingsConfig.find(c => c.id === f.feeling)
                                   return (
                                        <div key={idx} className="d-flex align-items-start gap-3 border-bottom border-gray-200 pb-2 last:border-0">
                                             <div className={`badge badge-circle badge-light-${config?.color} w-25px h-25px mt-1 shadow-xs`}>
                                                  <i className={clsx("fas fs-9", config?.icon, `text-${config?.color}`)} />
                                             </div>
                                             <div className="d-flex flex-column">
                                                  <div className="d-flex align-items-center gap-2">
                                                       <span className="fs-8 fw-boldest text-gray-800">{f.personnel?.prenomNom}</span>
                                                       <span className="fs-10 text-muted fw-bold">{f.createdAt?.format("DD/MM [à] HH:mm")}</span>
                                                  </div>
                                                  <span className="fs-8 text-gray-600 mt-1 italic">
                                                       {f.commentaire || <span className="text-gray-400 opacity-50">Aucun commentaire laissé</span>}
                                                  </span>
                                             </div>
                                        </div>
                                   )
                              })}
                         </div>
                    </div>
               )}
          </div>
     )
}

export default CandidatSuiviFeelingAction
